import { z } from 'zod/v4';
import type { components } from '@molchanovs/api-contract';

/** Existing canonical identity resolver, independent of learning/bootstrap projections. */
export const userProfileIdentityResponseSchema = z.object({
  data: z.object({
    profileId: z.uuid(),
    accountId: z.uuid().nullable(),
    bubbleUserId: z.string().min(1).nullable(),
  }),
});

/** Runtime parser for the API-owned stable IDs; Directory feature owns each view's behavior. */
export const crmDirectoryStartViewIdSchema = z.enum([
  'freedivers',
  'instructors',
  'activity',
  'membership',
  'shopify',
  'discounts',
  'documents',
]);

type ContractViewId = components['schemas']['CrmDirectoryStartViewIdV1'];
type RuntimeViewId = z.infer<typeof crmDirectoryStartViewIdSchema>;
type SameIds = [RuntimeViewId] extends [ContractViewId]
  ? [ContractViewId] extends [RuntimeViewId]
    ? true
    : false
  : false;
export const crmDirectoryStartViewIdContractParity: SameIds = true;

export const defaultApplicationStartViewSchema = z.discriminatedUnion('tab', [
  z.strictObject({
    route: z.literal('crm'),
    tab: z.literal('profiles'),
    view: crmDirectoryStartViewIdSchema.optional(),
  }),
  z.strictObject({
    route: z.literal('crm'),
    tab: z.literal('memberships'),
  }),
]);
export type DefaultApplicationStartView = z.infer<typeof defaultApplicationStartViewSchema>;

const userProfileContextRegistrationSchema = z.object({
  registration_id: z.string().min(1),
  number: z.string().nullable(),
  title: z.string().nullable(),
  course_id: z.string().nullable(),
  course_type_id: z.string().nullable(),
  is_current: z.boolean(),
  instructor: z.object({
    status: z.enum(['assigned', 'not_assigned', 'unavailable']),
    source: z
      .enum(['registration_submitting', 'registration_certifying', 'course_submitting'])
      .nullable(),
    user_id: z.string().nullable(),
    display_name: z.string().nullable(),
  }),
  certification: z.object({
    state: z.enum(['certified', 'available', 'unavailable', 'unknown']),
    certified_at: z.iso.datetime().nullable(),
    certification_type_id: z.string().nullable(),
  }),
});

export const userProfileContextResponseSchema = z.looseObject({
  data: z.object({
    profile_ref: z.string().min(1),
    current_registration: z.object({ registration_id: z.string().min(1) }).nullable(),
    current_training_program: z.object({ program_id: z.string().min(1) }).nullable(),
    onboarding: z
      .object({
        program_id: z.string().min(1).nullable(),
        assignment_id: z.string().min(1).nullable(),
      })
      .nullable(),
    default_application_start_view: defaultApplicationStartViewSchema.nullable(),
    registrations: z.array(userProfileContextRegistrationSchema).max(50),
    metadata: z.object({
      version: z.literal('user_context.v1'),
      updated_at: z.iso.datetime().nullable(),
    }),
  }),
});

export type UserProfileContextResponse = z.infer<typeof userProfileContextResponseSchema>;
export type UserProfileContext = UserProfileContextResponse['data'];
